import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 48);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-600 transition-colors"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-database-2-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold whitespace-nowrap">
              Hipobuy Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/what-is-hipobuy-spreadsheet"
              className="text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Guides
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Safety
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Hipobuy Sheet
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i
              className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.2s_ease-out] max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="px-4 py-5 space-y-5">

            <div className="flex items-center gap-3 pb-4 border-b border-background-200/70">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-xl">
                <i className="ri-database-2-line text-background-50 text-xl"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-900">Hipobuy Sheet</p>
                <p className="text-xs text-foreground-500">Product Discovery Database</p>
              </div>
            </div>

            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200/70">
                <i className="ri-home-4-line text-base text-foreground-600"></i>
              </span>
              Home
            </Link>

            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  Product Categories
                </p>
                <span className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                  {categories.length} categories
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-all active:scale-[0.97]"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-background-200/70 flex-shrink-0">
                      <i className={`${cat.icon} text-xs text-foreground-500`}></i>
                    </span>
                    <span className="truncate text-xs font-medium">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <Link
              to="/article/what-is-hipobuy-spreadsheet"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200/70">
                <i className="ri-book-open-line text-base text-foreground-600"></i>
              </span>
              Shopping Guides
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 bg-background-100 rounded-xl hover:bg-primary-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200/70">
                <i className="ri-shield-check-line text-base text-foreground-600"></i>
              </span>
              Safety Center
            </a>

            <div className="pt-2 flex flex-col gap-2 pb-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-semibold text-center text-background-50 bg-primary-500 hover:bg-primary-600 active:bg-primary-700 rounded-full transition-all whitespace-nowrap active:scale-[0.98]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Hipobuy Sheet
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full px-4 py-3 text-sm font-semibold text-center text-primary-600 border-2 border-primary-300 hover:bg-primary-50 active:bg-primary-100 rounded-full transition-all whitespace-nowrap active:scale-[0.98]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}